import { useState, useEffect } from 'react';
import { getCoupons, createCoupon, updateCoupon, deleteCoupon } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import styles from './AdminTable.module.css';

const EMPTY = {
  code: '', description: '', discountType: 'percentage', discountValue: '',
  minOrderAmount: 0, maxDiscount: '', usageLimit: '', isActive: true,
  expiresAt: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
};

export default function AdminCoupons() {
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);

  const fetch = () => getCoupons().then(r => setCoupons(r.data)).finally(() => setLoading(false));
  useEffect(() => { fetch(); }, []);

  const openCreate = () => { setForm(EMPTY); setEditCoupon(null); setShowModal(true); };
  const openEdit = (c) => {
    setForm({ ...c, expiresAt: c.expiresAt?.split('T')[0] || '', maxDiscount: c.maxDiscount || '', usageLimit: c.usageLimit || '' });
    setEditCoupon(c); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        discountValue: Number(form.discountValue),
        minOrderAmount: Number(form.minOrderAmount),
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : undefined,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
      };
      if (editCoupon) { await updateCoupon(editCoupon._id, payload); toast.success('Coupon updated!'); }
      else             { await createCoupon(payload);                  toast.success('Coupon created!'); }
      setShowModal(false);
      fetch();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"?`)) return;
    try { await deleteCoupon(id); toast.success('Deleted'); fetch(); }
    catch { toast.error('Failed'); }
  };

  const f = (field) => (e) => setForm(p => ({
    ...p,
    [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
  }));

  const isExpired = (date) => new Date(date) < new Date();

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Coupons</h2>
          <p className={styles.pageSubtitle}>{coupons.length} coupons</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus size={16}/> Add Coupon</button>
      </div>

      <div className={styles.tableCard}>
        {loading ? <div className="spinner-center"><div className="spinner"/></div> : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr><th>Code</th><th>Type</th><th>Value</th><th>Min Order</th><th>Used</th><th>Expires</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {coupons.length === 0 ? (
                  <tr><td colSpan={8} className={styles.emptyRow}>No coupons yet</td></tr>
                ) : coupons.map(c => (
                  <tr key={c._id}>
                    <td><span style={{ fontWeight:800, letterSpacing:'0.5px', color:'var(--primary)', fontFamily:'monospace', fontSize:'0.9rem' }}>{c.code}</span></td>
                    <td><span className={`badge ${c.discountType==='percentage' ? 'badge-info' : 'badge-primary'}`}>{c.discountType}</span></td>
                    <td><strong>{c.discountType==='percentage' ? `${c.discountValue}%` : `৳${c.discountValue}`}</strong></td>
                    <td>৳{c.minOrderAmount?.toLocaleString()}</td>
                    <td>{c.usedCount}{c.usageLimit ? ` / ${c.usageLimit}` : ''}</td>
                    <td style={{ fontSize:'0.8rem', color: isExpired(c.expiresAt) ? 'var(--error)' : 'var(--text-muted)' }}>
                      {isExpired(c.expiresAt) ? '⚠️ ' : ''}{formatDate(c.expiresAt)}
                    </td>
                    <td>
                      <span className={`badge ${c.isActive && !isExpired(c.expiresAt) ? 'badge-success' : 'badge-error'}`}>
                        {c.isActive && !isExpired(c.expiresAt) ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionBtns}>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(c)}><FiEdit2 size={14}/></button>
                        <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }} onClick={() => handleDelete(c._id, c.code)}><FiTrash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} style={{ maxWidth:520 }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editCoupon ? 'Edit Coupon' : 'Create Coupon'}</h3>
              <button className={styles.modalClose} onClick={() => setShowModal(false)}><FiX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className={styles.modalForm}>
              <div className={styles.formGrid2}>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Coupon Code *</label>
                  <input className="form-input" value={form.code} onChange={f('code')} required placeholder="e.g. EID50" style={{ textTransform:'uppercase', fontFamily:'monospace', fontWeight:700 }}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Type *</label>
                  <select className="form-input form-select" value={form.discountType} onChange={f('discountType')}>
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (৳)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount Value *</label>
                  <input type="number" className="form-input" value={form.discountValue} onChange={f('discountValue')} required min={1} placeholder={form.discountType==='percentage' ? '10' : '100'}/>
                </div>
                <div className="form-group">
                  <label className="form-label">Minimum Order (৳)</label>
                  <input type="number" className="form-input" value={form.minOrderAmount} onChange={f('minOrderAmount')} min={0} placeholder="0"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Max Discount (৳)</label>
                  <input type="number" className="form-input" value={form.maxDiscount} onChange={f('maxDiscount')} min={0} placeholder="Optional cap"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Usage Limit</label>
                  <input type="number" className="form-input" value={form.usageLimit} onChange={f('usageLimit')} min={1} placeholder="Unlimited"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Expires At *</label>
                  <input type="date" className="form-input" value={form.expiresAt} onChange={f('expiresAt')} required/>
                </div>
                <div className="form-group" style={{ gridColumn:'1/-1' }}>
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description} onChange={f('description')} placeholder="Eid special 50% off"/>
                </div>
                <label className={styles.checkbox}>
                  <input type="checkbox" checked={form.isActive} onChange={f('isActive')}/> Active
                </label>
              </div>
              <div className={styles.modalFooter}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editCoupon ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
